import React, { useEffect } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { bindActionCreators } from "redux";

// MUI Components
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MobileStepper from "@material-ui/core/MobileStepper";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";

// Icon
import CircularProgress from "@material-ui/core/CircularProgress";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import CheckIcon from "@material-ui/icons/Check";
import WarningIcon from "@material-ui/icons/Warning";

// Actions
import {
  fetchCloseDevice,
  toggleDiagnosticsDialog,
  requestHealth,
  toggleSkipDiagnosticsSetup,
  fetchSetLocation,
} from "../../actions";

import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import classNames from "classnames";

import rotateDown from "../../images/gelCapIllustrations/rotateDown.gif";
import capOn from "../../images/gelCapIllustrations/capOn.gif";
import pushBubble from "../../images/gelCapIllustrations/pushBubble.png";
import holster from "../../images/gelCapIllustrations/holster.png";

const useStyles = makeStyles({
  root: (props) => ({
    ...customTheme(props.theme).dialog.root,
    minHeight: "600px",
    minWidth: "800px",
    ...customTheme(props.theme).customScrollBar,
    overflowY: "auto",
  }),
  title: customTheme().dialog.title,
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    overflowY: "unset",
    alignItems: "center",
    width: "100%",
  },
  buttonContainer: {
    ...customTheme().dialog.buttonContainer,
    paddingTop: "0px",
    padding: "0px",
  },
  stepLabel: {
    width: "100%",
    padding: "0px 10px 10px 10px",
    backgroundColor: (props) => colors[props.theme].dialogBkg,
  },
  icon: {
    ...customTheme().icon,
    ...customTheme().iconLarge,
  },
  stepContent: {
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  progressContent: {
    display: "flex",
    padding: "100px",
    alignItems: "center",
    flexDirection: "row",
  },

  buttonOutlined: (props) => ({
    minWidth: "150px",
    ...customTheme(props.theme).buttonOutlined,
  }),
  buttonPrimary: (props) => ({
    minWidth: "150px",
    ...customTheme(props.theme).buttonPrimary,
  }),
  setupInstructions: {
    padding: "10px",
  },
  illustrationContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    alignSelf: "center",
  },
  illustrationStepper: {
    width: "60%",
    padding: "0px",
    backgroundColor: (props) => colors[props.theme].dialogBkg,
  },
  illustrationText: {
    fontSize: "0.875rem",
    paddingTop: "20px",
  },
  illustrationImage: {
    maxWidth: "85%",
    maxHeight: "50%",
  },
  checkcon: {
    ...customTheme().icon,
    ...customTheme().iconLarge,
    fill: (props) => colors[props.theme].primary,
  },
  warningIcon: {
    ...customTheme().icon,
    ...customTheme().iconLarge,
    fill: colors.warning,
  },
  initialButtonContainer: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
    padding: "5px",
  },
  warningButtonContainer: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
    padding: "10px",
  },
  errorInformationDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "50px",
  },
  errorInformationSubDiv: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  customErrorText: {
    padding: "10px",
  },
  countdown: {
    textAlign: "center",
    marginLeft: "20px",
    marginRight: "20px",
    position: "relative",
  },
  seconds: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    fontSize: "2.0rem",
  },
});

let countdownTimer;

const DiagnosticsDialog = (props) => {
  const classes = useStyles(props);

  const totalSeconds = 3;
  const [activeStep, setActiveStep] = React.useState(0);
  const [illustrationStep, setIllustrationStep] = React.useState(0);
  const [seconds, setSeconds] = React.useState(totalSeconds);
  const stepLabels = ["Setup", "Check", "Done"];
  const illustrationImages = [
    // {
    //   text:
    //     "If there is a used gel cap on the handle, remove and discard the used gel cap before inserting a new one. To remove the gel cap, turn the dial counter-clockwise for quarter of a turn. The gel cap will be ejected as the platform ascends. Remove and discard the gel cap.",
    //   image: capOff,
    // },
    {
      text: "Check that the transducer face is clean and ensure the dial is rotated all the way clockwise. The platform should descend.",
      image: rotateDown,
    },
    {
      text: "Put a pea-sized drop of ultrasound gel on the face of the transducer, and then place the gel cap over the transducer.",
      image: capOn,
    },
    {
      text: "After placing the gel cap, gently press on the gel cap to push out any air bubbles between the gel cap and the transducer.",
      image: pushBubble,
    },
    {
      text: 'Place the handle in the holster. Make sure there is no object blocking the transducer. There should be 1" of space in front of the transducer.',
      image: holster,
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setIllustrationStep(0);
    props.requestHealth();
  };

  const handleFinish = () => {
    clearInterval(countdownTimer); // clear timer

    props.toggleDiagnosticsDialog();
    setActiveStep(0);
    setIllustrationStep(0);
  };

  const handleRerun = () => {
    clearInterval(countdownTimer); // clear timer

    setActiveStep(1);
    setIllustrationStep(0);
    props.requestHealth();
  };

  const handleStartOver = () => {
    clearInterval(countdownTimer); // clear timer
    setActiveStep(0);
    setIllustrationStep(0);
  };

  const handleCancelDiagnostics = () => {
    setActiveStep(0);
    setIllustrationStep(0);
    if (props.connectionEstablished && !props.health.checked) {
      props.fetchCloseDevice();
      props.toggleDiagnosticsDialog();
    } else {
      props.toggleDiagnosticsDialog();
    }
  };

  useEffect(() => {
    if (props.health.checked) {
      setActiveStep(2);
      setSeconds(totalSeconds);
      if (props.skipDiagnosticsSetup) {
        props.toggleSkipDiagnosticsSetup();
      }
    }
  }, [props.health.checked]);

  useEffect(() => {
    if (activeStep === 2 && props.health.status && props.health.checked) {
      countdownTimer = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        } else {
          handleFinish();
        }
      }, 1000);
    }

    return () => clearInterval(countdownTimer);
  }, [activeStep, seconds]);

  useEffect(() => {
    if (props.skipDiagnosticsSetup) {
      setActiveStep(1);
      props.requestHealth();
    }
  }, [props.skipDiagnosticsSetup]);

  const handleIllustrationNext = () => {
    setIllustrationStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleIllustrationBack = () => {
    setIllustrationStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    props.fetchSetLocation(
      props.selectedLocationId,
      props.currentAcqType,
      true
    );
  };

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <React.Fragment>
            <Typography className={classes.setupInstructions}>
              To setup the device, insert a new gel cap in the handle, place the
              handle in the holster, and click continue.
            </Typography>
            <div className={classes.initialButtonContainer}>
              <Button
                onClick={() => handleCancelDiagnostics()}
                className={classes.buttonOutlined}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNext()}
                className={classes.buttonPrimary}
              >
                Continue
              </Button>
            </div>
            <Typography className={classes.setupInstructions}>
              For detailed instructions on the device setup, follow the steps
              below.
            </Typography>
            <div className={classes.illustrationContainer}>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <img
                  className={classes.illustrationImage}
                  src={illustrationImages[illustrationStep].image}
                  alt={"Gel cap illustration"}
                />
                <Typography className={classes.illustrationText}>
                  {illustrationImages[illustrationStep].text}
                </Typography>
              </div>
              <MobileStepper
                variant="dots"
                steps={illustrationImages.length}
                position="static"
                activeStep={illustrationStep}
                className={classes.illustrationStepper}
                nextButton={
                  <Button
                    size="small"
                    onClick={() => handleIllustrationNext()}
                    disableRipple
                    disabled={
                      illustrationStep === illustrationImages.length - 1
                    }
                  >
                    <KeyboardArrowRight />
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    onClick={() => handleIllustrationBack()}
                    disabled={illustrationStep === 0}
                    disableRipple
                  >
                    <KeyboardArrowLeft />
                  </Button>
                }
              />
            </div>
          </React.Fragment>
        );
      case 1:
        return (
          <div className={classes.progressContent}>
            <CircularProgress className={classes.icon} size={30} />
            <Typography>Running check...</Typography>
          </div>
        );
      case 2:
        if (props.health.status) {
          return (
            <React.Fragment>
              <div className={classes.progressContent}>
                <CheckIcon className={classes.checkcon} size={40} />
                <Typography>
                  Ready to acquire samples
                  <br />
                  {`Gel cap height: ${
                    props.health.capHeight === null
                      ? "-"
                      : props.health.capHeight + " mm"
                  }`}
                  <br />
                  {`SWS: ${
                    props.health.sws === null || props.health.sws === undefined
                      ? "-"
                      : props.health.sws + " m/s"
                  }`}
                </Typography>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment>
              <div className={classes.warningButtonContainer}>
                <Button
                  onClick={() => handleStartOver()}
                  variant="outlined"
                  className={classes.buttonOutlined}
                >
                  Start Over
                </Button>
                <Button
                  onClick={() => handleRerun()}
                  variant="contained"
                  color="primary"
                  className={classes.buttonPrimary}
                >
                  Rerun Check
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => handleFinish()}
                  className={classes.buttonOutlined}
                >
                  Ignore Warning
                </Button>
              </div>
              <div className={classes.errorInformationDiv}>
                <div className={classes.errorInformationSubDiv}>
                  <WarningIcon className={classes.warningIcon} size={35} />
                  <Typography>
                    The system did not successfully detect the gel cap. Make
                    sure the gel cap is attached, no air bubbles are present,
                    and click "re-run check". If this problem persists, please
                    contact support.
                  </Typography>
                </div>
                <Typography className={classes.customErrorText}>
                  {props.health.message}
                </Typography>
              </div>
            </React.Fragment>
          );
        }
      default:
        return "Unknown stepIndex";
    }
  }
  return (
    <Dialog
      open={props.open}
      PaperProps={{ className: classNames(classes.root) }}
      onExit={() => handleClose()}
    >
      <Typography
        className={classes.title}
        variant="h4"
        color="textPrimary"
        align="center"
      >
        Enter Acquisition Mode
      </Typography>
      <DialogContent className={classes.contentContainer}>
        <Stepper
          className={classes.stepLabel}
          activeStep={activeStep}
          alternativeLabel
        >
          {stepLabels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <div className={classes.stepContent}>
          {props.connectionEstablished ? (
            getStepContent(activeStep)
          ) : (
            <div className={classes.progressContent}>
              <CircularProgress className={classes.icon} size={30} />
              <Typography>Connecting...</Typography>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        {props.connectionEstablished &&
          activeStep === stepLabels.length - 1 &&
          props.health.status && (
            <React.Fragment>
              <Button
                variant="outlined"
                onClick={() => handleRerun()}
                className={classes.buttonOutlined}
              >
                Rerun Check
              </Button>

              <div className={classes.countdown}>
                <CircularProgress
                  size={70}
                  variant="static"
                  value={(seconds / totalSeconds) * 100}
                  className={classes.progress}
                  style={{ transform: "scaleX(-1) rotate(-90deg)" }}
                />
                <div className={classes.seconds}>{Math.ceil(seconds)}</div>
              </div>

              <Button
                onClick={() => handleFinish()}
                variant="contained"
                color="primary"
                className={classes.buttonPrimary}
              >
                Done
              </Button>
            </React.Fragment>
          )}
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchCloseDevice,
      toggleDiagnosticsDialog,
      requestHealth,
      toggleSkipDiagnosticsSetup,
      fetchSetLocation,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    diagnosticsDialog: state.utility.dialog.diagnosticsDialog,
    theme: state.utility.theme,
    connectionEstablished: state.device.connectionEstablished,
    health: state.device.health,
    skipDiagnosticsSetup: state.utility.skipDiagnosticsSetup,
    selectedLocationId: state.locations.selectedLocationId,
    currentAcqType: state.locations.currentAcqType,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DiagnosticsDialog);
